import React from 'react';
import { formatNumberValue } from '../utils/format';
import { Box, Tooltip, Typography } from '@mui/material';
import { EmptyState } from '../feedback/EmptyState';

export interface BarListRow {
  key: string;
  label: string;
  value: number | null;
  /** A second figure shown in its own column, e.g. the count behind a rate. */
  secondary?: number | null;
  /** Greyed out: a figure that rests on too little to read like the others. */
  muted?: boolean;
  /** Replaces the tooltip's text, e.g. to say why a row is greyed out. */
  hint?: string;
}

export interface BarListProps {
  rows: BarListRow[];
  formatValue: (value: number) => string;
  /** Column heading for the bars, e.g. "Placement rate". */
  valueLabel: string;
  /** Column heading for the names. */
  labelHeading?: string;
  /** Column heading for `secondary`. Omit to hide the column. */
  secondaryLabel?: string;
  formatSecondary?: (value: number) => string;
  /** Headings to draw instead of the plain labels — a `SortHeader`, say. */
  valueHeading?: React.ReactNode;
  secondaryHeading?: React.ReactNode;
  /** More rows than this split into two columns when there is room. */
  splitAfter?: number;
  emptyMessage?: string;
}

// Wide enough for two readable columns of label, bar and figures.
const TWO_COLUMNS_FROM = 760;

/**
 * A ranked list of horizontal bars: one value per row, all on one scale.
 *
 * Figures sit in their own right-aligned columns so they read down like a
 * table, not scattered at the ends of bars. An optional second column carries
 * the count behind each value — a rate means little without it.
 *
 * Every bar is scaled against the largest value in the whole list, so when a
 * long list splits into two columns, the columns stay comparable. The split
 * follows the list's own width, not the window's.
 */
export function BarList({
  rows,
  formatValue,
  valueLabel,
  labelHeading = 'Name',
  secondaryLabel,
  formatSecondary = formatNumberValue,
  valueHeading,
  secondaryHeading,
  splitAfter = 6,
  emptyMessage = 'Nothing to show for this selection.',
}: BarListProps) {
  if (!rows.length) return <EmptyState message={emptyMessage} height={120} />;

  // Greyed rows don't set the scale: one agent at 18% on a handful of leads
  // would shrink every reliable bar to a sliver. They are capped at full width.
  const scaled = rows.some((r) => !r.muted && r.value !== null) ? rows.filter((r) => !r.muted) : rows;
  const values = scaled.map((r) => r.value).filter((v): v is number => v !== null);
  const max = values.length ? Math.max(...values) : 0;
  const split = rows.length > splitAfter;
  const hasSecondary = Boolean(secondaryLabel);
  // The figure and the count it rests on sit together in one column, so they
  // read as one thing: "2.6% (4,269)".
  const columns = hasSecondary
    ? 'minmax(96px, 30%) minmax(0, 1fr) minmax(128px, auto)'
    : 'minmax(96px, 30%) minmax(0, 1fr) 72px';

  const header = (second?: boolean) => (
    <Box
      className={second ? 'KitBarList-secondHeader' : undefined}
      sx={(t) => ({
        display: 'grid',
        gridTemplateColumns: columns,
        columnGap: 1.5,
        pb: 0.75,
        mb: 0.5,
        borderBottom: `1px solid ${t.tokens.border.subtle}`,
        '& > *': { color: t.tokens.text.tertiary },
      })}
    >
      <Typography variant="overline" noWrap>{labelHeading}</Typography>
      {valueHeading ?? <Typography variant="overline" noWrap>{valueLabel}</Typography>}
      {hasSecondary ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, justifyContent: 'flex-end' }}>
          {secondaryHeading ?? <Typography variant="overline" noWrap>{secondaryLabel}</Typography>}
        </Box>
      ) : (
        <Box />
      )}
    </Box>
  );

  return (
    <Box sx={{ containerType: 'inline-size' }}>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'minmax(0, 1fr)',
          columnGap: 4,
          '& .KitBarList-secondHeader': { display: 'none' },
          [`@container (min-width: ${TWO_COLUMNS_FROM}px)`]: split
            ? {
                gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                '& .KitBarList-secondHeader': { display: 'grid' },
              }
            : {},
        }}
      >
        {header()}
        {split && header(true)}
      </Box>

      <Box
        sx={{
          columnGap: 4,
          [`@container (min-width: ${TWO_COLUMNS_FROM}px)`]: split ? { columnCount: 2 } : {},
        }}
      >
        {rows.map((row) => {
          const width = max > 0 && row.value !== null ? Math.min(100, Math.max(1, (row.value / max) * 100)) : 0;
          const shown = row.value === null ? '—' : formatValue(row.value);
          return (
            <Tooltip
              key={row.key}
              placement="top"
              title={
                row.hint ?? `${row.label}: ${shown}`
                + (hasSecondary && row.secondary != null ? ` · ${formatSecondary(row.secondary)} ${secondaryLabel?.toLowerCase()}` : '')
              }
            >
              <Box
                sx={(t) => ({
                  display: 'grid',
                  gridTemplateColumns: columns,
                  columnGap: 1.5,
                  alignItems: 'center',
                  py: 0.5,
                  breakInside: 'avoid',
                  borderRadius: `${t.ds.radius.xs}px`,
                  '&:hover': { bgcolor: t.tokens.brand.primarySofter },
                })}
              >
                {/* A long name wraps under itself rather than being cut. */}
                <Typography
                  variant="body2"
                  sx={(t) => ({
                    color: row.muted ? t.tokens.text.tertiary : t.tokens.text.secondary, minWidth: 0,
                    overflowWrap: 'anywhere',
                  })}
                >
                  {row.label}
                </Typography>
                <Box sx={(t) => ({ height: 12, borderRadius: `${t.ds.radius.xs}px`, bgcolor: t.tokens.surface.sunken })}>
                  <Box
                    sx={(t) => ({
                      height: '100%',
                      width: `${width}%`,
                      borderRadius: `${t.ds.radius.xs}px`,
                      bgcolor: row.muted ? t.tokens.border.default : t.tokens.brand.primary,
                      transition: `width ${t.ds.motion.duration.slow}ms ${t.ds.motion.easing.standard}`,
                    })}
                  />
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'flex-end', gap: 0.5 }}>
                  <Typography
                    variant="body2"
                    sx={(t) => ({
                      fontWeight: 600,
                      fontVariantNumeric: 'tabular-nums',
                      color: row.muted ? t.tokens.text.tertiary : t.tokens.text.primary,
                    })}
                  >
                    {shown}
                  </Typography>
                  {hasSecondary && (
                    <Typography
                      variant="caption"
                      sx={(t) => ({ color: t.tokens.text.tertiary, fontVariantNumeric: 'tabular-nums' })}
                    >
                      ({row.secondary == null ? '—' : formatSecondary(row.secondary)})
                    </Typography>
                  )}
                </Box>
              </Box>
            </Tooltip>
          );
        })}
      </Box>
    </Box>
  );
}
